import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	SortingState,
	AccessorKeyColumnDef,
	ColumnFiltersState,
} from '@tanstack/react-table'
import { RefObject, useMemo, useEffect, useState, ReactNode } from 'react'
import { HubConnection } from '@microsoft/signalr'
import {
	CopyAllOutlined,
	Delete,
	DriveFileMoveOutlined,
	Edit,
	FileDownloadOutlined,
	FilterAltOff,
	InfoOutlined,
	Key,
	KeyboardArrowDown,
	KeyboardArrowUp,
	LocalOffer,
	Lock,
	ManageHistory,
	Star,
	StarOutlineOutlined,
	UnfoldMore,
	Visibility,
	IosShareOutlined,
	StickyNote2Sharp,
	Link,
	Refresh,
	AutoAwesome,
} from '@mui/icons-material'
import { useNavigate, useOutletContext } from 'react-router-dom'
import dayjs from 'dayjs'
import { DocumentData } from '../../lib/api/types/document.interfaces'
import { useUserDateFormat } from '../useUserDateFormat'
import {
	Checkbox,
	EllipsisText,
	IconButton,
	OptionsMenuButton,
	Tooltip,
} from '../../components/ui'
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'
import { openAuraEdit, useToggleObjectFavorite } from '../api/documentHooks'
import cn from '../../lib/utils/cn'
import { isFileNameEsignSupported } from '../../lib/utils/esign'
import { MainLayoutType } from '../../components/layouts/MainLayout'
import {
	EditingItem,
	CustomColumnFilter,
	DataTableEntry,
} from '../../pages/Workspace/components/interfaces.table'
import {
	formatDocumentSize,
	processingStatuses,
	shortenFileName,
} from '../../lib/utils/formatDocument'
import FilterButton, {
	FILTERED_OBJECT_TYPE_VALUES,
} from '../../components/table/FilterButton'
import {
	useCustomGlobalFilter,
	useDateFilter,
	useSizeFilter,
	useTextFilter,
	useObjectIdFilter,
	useTypeFilter,
	useStatusFilter,
	FILTER_TYPES,
} from './useColumnFilters'
import FileFormatIcon from '../../components/common/FileFormatIcon'
import { getFileNameAndExt } from '../../lib/utils/formatDocument'
import ProcessingStatusIcon from '../../pages/Workspace/components/ProcessingStatusIcon'
import { Features, hasFeature, useBilling } from '../../context/billingContext'
import { FilteredObjectType } from '../../components/table/FilterButton'
import { ReactComponent as SigningSymbol } from '../../assets/icons/interactions/signing-symbol.svg'
import { useAuth } from '../../hooks'

interface useCreateRecentDataTableProps {
	data?: DocumentData[] | null
	visibleColumns: string[]
	selectedMobileColumn?: string
	containerRef?: RefObject<HTMLDivElement>
	searchQuery?: string
	limit?: number
	offset?: number
	onToggleLock: (id: string, value: boolean) => void
	onToggleItem?: (
		id: string,
		type: number,
		checked: boolean,
		name?: string,
		fileType?: string,
		isFavorite?: boolean,
	) => void
	onToggleAll?: (
		ids: { id: string; type: number; name?: string; isFavorite?: boolean }[],
		checked: boolean,
	) => void
	onDeleteItem: (item: EditingItem) => void
	onEditItem: (item: EditingItem) => void
	onPreview: (id: string, versionId?: string | null, isBlank?: boolean) => void
	onFileInfo: (id: string) => void
	onMove: (id: string, type: 'copy' | 'move') => void
	onConfigPermissions: (id: string) => void
	onConfigVersion: (id: string) => void
	onDownload: (id: string) => void
	onReclassify: (id: string) => void
	onCopyLink?: (id: string, type?: number) => void
	onAddNote?: (id: string) => void
	onSendToESign?: (id: string) => void
	onChangeFilters: (newFilters: CustomColumnFilter[]) => void
	checkedItems: {
		id: string
		type: number
	}[]
	onShareItem: (item: EditingItem) => void
	lockedFiles: Record<string, boolean>
	columnFilters: CustomColumnFilter[]
	isSocketConnected?: boolean
	isMobile?: boolean
}

const customGlobalFilter = useCustomGlobalFilter()
const dateFilter = useDateFilter()
const sizeFilter = useSizeFilter()
const textFilter = useTextFilter()
const objectIdFilter = useObjectIdFilter()
const typeFilter = useTypeFilter()
const statusFilter = useStatusFilter()

const getSortedItems = (
	sorting: SortingState,
	items: DataTableEntry[] = [],
) => {
	const sort = sorting?.[0]
	let sorted = items

	if (sort) {
		const { id: columnId, desc } = sort
		sorted = [...items].sort((a, b) => {
			const aValue = a[columnId as keyof typeof a] ?? ''
			const bValue = b[columnId as keyof typeof b] ?? ''

			// Use dayjs for date comparison
			if (
				typeof columnId === 'string' &&
				(columnId.toLowerCase().includes('date') ||
					columnId.toLowerCase().includes('at'))
			) {
				const isValidDate = (val: any): val is string | number | Date =>
					val !== null &&
					(val instanceof Date ||
						typeof val === 'string' ||
						typeof val === 'number')

				if (isValidDate(aValue) && isValidDate(bValue)) {
					const aDate = dayjs(aValue as string | number | Date)
					const bDate = dayjs(bValue as string | number | Date)
					if (aDate.isValid() && bDate.isValid()) {
						return desc
							? bDate.valueOf() - aDate.valueOf()
							: aDate.valueOf() - bDate.valueOf()
					}
				}
			}

			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return desc ? bValue - aValue : aValue - bValue
			}
			const cmp = String(aValue).localeCompare(String(bValue))
			return desc ? -cmp : cmp
		})
	}

	const folders = sorted.filter((item) => item.objectType === 1)
	const files = sorted.filter((item) => item.objectType === 0)
	return [...folders, ...files]
}

export const useCreateRecentDataTable = ({
	data = [],
	visibleColumns,
	selectedMobileColumn,
	containerRef,
	searchQuery,
	limit = 10,
	offset = 0,
	onToggleLock,
	onToggleItem,
	onToggleAll,
	onDeleteItem,
	onEditItem,
	onPreview,
	onFileInfo,
	onConfigPermissions,
	onConfigVersion,
	onDownload,
	onMove,
	onReclassify,
	onCopyLink,
	onAddNote,
	onSendToESign,
	onChangeFilters,
	onShareItem,
	checkedItems,
	lockedFiles,
	columnFilters,
	isSocketConnected,
	isMobile,
}: useCreateRecentDataTableProps) => {
	const navigate = useNavigate()
	const { hasFeature } = useBilling()
	const { formatDate, formatDateTime } = useUserDateFormat()

	const { currentUser } = useAuth()
	const { isApp } = useOutletContext<MainLayoutType>()
	const hideESignActions = Boolean(isApp || isMobile)
	const [sorting, setSorting] = useState<SortingState>([])

	const { mutateAsync: toggleFavorite } = useToggleObjectFavorite()

	const handleEditDocument = (fileId: string, fileName?: string) => {
		openAuraEdit(fileId, fileName)
	}

	const handleToggleFavorite = async (item: {
		id: string
		type: number
		value: boolean
	}) => {
		await toggleFavorite({ data: item, invalidate: true })
	}

	const renderSortingHeader = (
		title: string,
		column: React.Column<DataTableEntry, unknown>,
		className?: string,
		leftIcon?: ReactNode,
	) => {
		const isObject = [
			'modifiedBy',
			'createdBy',
			'category',
			'subcategory',
		]?.includes(column?.id)

		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 box-border  dark:text-text-table-dark min-h-full',
					className,
				)}
			>
				<FilterButton
					columnKey={column?.id}
					setColumnFilters={onChangeFilters}
					columnFilters={columnFilters}
					isDate={['createdAt', 'modifiedAt']?.includes(column?.id)}
					isSize={column?.id === 'size'}
					objectType={
						['modifiedBy', 'createdBy']?.includes(column?.id)
							? 'user'
							: FILTERED_OBJECT_TYPE_VALUES?.includes(
										column?.id as FilteredObjectType,
								  )
								? (column.id as FilteredObjectType)
								: null
					}
					initialFilterType={
						isObject
							? FILTER_TYPES.OBJECT_ID
							: column?.id === 'processingStatus'
								? FILTER_TYPES.NUMBER
								: null
					}
					checkboxesItems={
						column?.id === 'processingStatus'
							? Object?.entries(processingStatuses)?.map(([k, v]) => ({
									id: k,
									label: v,
								}))
							: null
					}
				/>

				<EllipsisText
					tooltipLabel={title}
					text={title}
					leftIcon={leftIcon}
					className="text-center w-fit max-w-auto truncate"
				/>
				<span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5',
						column.getIsSorted() === false && 'hidden group-hover:inline-block',
					)}
				>
					{column.getIsSorted() === 'asc' && (
						<KeyboardArrowUp className="w-5 h-5" />
					)}
					{column.getIsSorted() === 'desc' && (
						<KeyboardArrowDown className="w-5 h-5" />
					)}
					{column.getIsSorted() === false && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span>
			</div>
		)
	}

	const tableData = useMemo(() => {
		const safeData = Array.isArray(data) ? data : []
		const mapped = safeData
			?.map((entry) => ({
				id: entry.id,
				name: entry.name,
				createdBy: entry?.createdBy?.name || entry?.createdBy?.email,
				createdByObject: entry?.createdBy,
				createdAt: entry?.createdAt || null,
				type: entry?.fileType
					? entry?.fileType
					: entry?.type
						? 'Folder'
						: entry?.name?.split('.')?.reverse()?.[0]?.toUpperCase(),
				objectType: entry?.type,
				size: entry?.size,
				category: entry?.category?.name ? entry?.category?.name : '-',
				categoryObject: entry?.category,
				subcategory: entry?.subcategory?.name ? entry?.subcategory?.name : '-',
				subcategoryObject: entry?.subcategory,
				modifiedBy: entry?.modifiedBy?.name || entry?.modifiedBy?.email || '',
				modifiedByObject: entry?.modifiedBy,
				modifiedAt: entry?.modifiedAt || '',
				processingStatus:
					entry?.processingStatus === 0 ? 2 : entry?.processingStatus || -1,
				lock: entry?.lock,
				options: {
					id: entry.id,
					isFavorite: entry?.isFavorite,
					isLocked: Boolean(
						typeof lockedFiles[entry?.id] === 'boolean'
							? lockedFiles[entry?.id]
							: entry?.lock?.id,
					),
					isSigned: entry?.isSigned,
				},
			}))
			?.filter((item) => currentUser?.organization?.sharedFolderId !== item?.id)

		return getSortedItems(sorting, mapped)
	}, [data, lockedFiles, sorting, currentUser])

	const columns = useMemo<ColumnDef<DataTableEntry>[]>(() => {
		const base: ColumnDef<DataTableEntry>[] = [
			{
				accessorKey: 'name',
				enableResizing: true,
				enableSorting: true,
				minSize: 140,
				size: isMobile ? 140 : 200,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => {
					const rows = table.getRowModel().rows
					const allIds = rows
						?.filter(
							(row) =>
								!lockedFiles[row?.original?.id] &&
								!row?.original?.options?.isLocked,
						)
						.map((row) => ({
							id: row.original.id,
							type: row.original.type,
							name: row.original.name,
							isFavorite: row.original.options?.isFavorite,
						}))

					const isAllSelected = Boolean(
						allIds?.length &&
						allIds?.every(({ id }) =>
							checkedItems?.find((item) => item?.id === id),
						),
					)
					return (
						<div className="h-full flex items-center gap-3 max-w-full pl-3 md:pl-6 group cursor-pointer dark:text-text-table-dark">
							<Checkbox
								checked={isAllSelected}
								disabled={!allIds?.length}
								onChange={() => onToggleAll?.(allIds, !isAllSelected)}
							/>
							{renderSortingHeader('Name', column, 'w-fit pl-2')}
						</div>
					)
				},
				cell: ({ row }) => {
					const name = row?.original?.name || ''
					const isChecked = checkedItems?.some(
						({ id }) => id === row?.original?.id,
					)
					const isLocked = row?.original?.options?.isLocked
					const isSigned = row?.original?.options?.isSigned

					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis">
							<div
								className="pl-3 md:pl-6 h-14 flex justify-center items-center"
								onClick={(e) => {
									e?.stopPropagation()
									if (isLocked) {
										Boolean(
											row?.original?.lock?.id === currentUser?.id ||
											currentUser?.organization?.isAdministrator,
										) && onToggleLock(row?.original?.id, false)
										return
									}
									onToggleItem?.(
										row.original.id,
										row.original.objectType,
										!isChecked,
										row?.original?.name,
										row?.original?.type,
										row.original?.options?.isFavorite,
									)
								}}
							>
								{isLocked ? (
									<IconButton
										variant="secondary"
										className="w-5 h-5 min-w-5 min-h-5 mr-3 rounded-md p-0 bg-background-light-hover hover:bg-background-light-hover"
										icon={<Lock className="w-3.5 h-3.5 fill-text-secondary" />}
										tooltip={
											row?.original?.lock?.createdBy?.id
												? `Locked by ${
														row?.original?.lock?.createdBy?.id ===
														currentUser?.id
															? 'you'
															: (row?.original?.lock?.createdBy?.name ??
																row?.original?.lock?.createdBy?.email)
													}`
												: 'Unlock'
										}
										onClick={() => {
											if (
												row?.original?.lock?.id === currentUser?.id ||
												currentUser?.organization?.isAdministrator
											) {
												onToggleLock(row?.original?.id, false)
											}
										}}
										disabled={
											currentUser?.id !== row.original?.lock?.createdBy?.id &&
											!currentUser?.organization?.isAdministrator
										}
									/>
								) : (
									<Checkbox
										checked={isChecked}
										disabled={isLocked}
										onChange={(_, event) => {
											event?.stopPropagation()
											onToggleItem?.(
												row.original.id,
												row.original.objectType,
												!isChecked,
												row?.original?.name,
												row?.original?.type,
												row.original?.options?.isFavorite,
											)
										}}
										className="mr-3"
									/>
								)}
							</div>
							<FileFormatIcon
								extension={getFileNameAndExt(name).ext}
								isFolder={!!row?.original?.objectType}
								className="w-5 h-5"
								isSigned={isSigned}
							/>
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'createdBy',
				enableResizing: true,
				enableSorting: true,
				minSize: 90,
				size: 90,
				sortingFn: 'alphanumeric',
				filterFn: objectIdFilter,
				header: ({ column }) => renderSortingHeader('Creator', column),
				cell: (info) => (
					<p className="truncate">{String(info.getValue() ?? '')}</p>
				),
			},
			{
				accessorKey: 'createdAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				sortingFn: 'datetime',
				filterFn: dateFilter,
				header: ({ column }) => renderSortingHeader('Creation Date', column),
				cell: (info) => {
					const val = info.getValue()
					if (!val) return '-'
					const str = String(val)
					return (
						<Tooltip
							label={formatDateTime(str)}
							classNameChildrenWrapper="w-fit"
						>
							<span>{formatDate(str)}</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'modifiedAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				sortingFn: 'datetime',
				filterFn: dateFilter,
				header: ({ column }) => renderSortingHeader('Modified Date', column),
				cell: (info) => {
					const val = info.getValue()
					if (!val) return '-'
					const str = String(val)
					return (
						<Tooltip
							label={formatDateTime(str)}
							classNameChildrenWrapper="w-fit"
						>
							<span>{formatDate(str)}</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'modifiedBy',
				enableResizing: true,
				enableSorting: true,
				minSize: 90,
				size: 90,
				sortingFn: 'alphanumeric',
				filterFn: objectIdFilter,
				header: ({ column }) => renderSortingHeader('Modifier', column),
				cell: (info) => (
					<p className="truncate">{String(info.getValue() ?? '')}</p>
				),
			},
			{
				accessorKey: 'processingStatus',
				enableResizing: true,
				enableSorting: true,
				minSize: 80,
				size: 80,
				sortingFn: 'alphanumeric',
				filterFn: statusFilter,
				header: ({ column }) =>
					renderSortingHeader(
						'Status',
						column,
						'',
						<AutoAwesome className="w-4 h-4" />,
					),
				cell: ({ row }) => {
					const { objectType, processingStatus, size } = row?.original
					const statusKey = Number(processingStatus)
					return (
						<ProcessingStatusIcon
							status={statusKey}
							isEmptyFile={!size}
							objectType={objectType}
						/>
					)

					// return row?.original?.objectType ? (
					// 	<Tooltip
					// 		label={"Folder can't have a status, but its items can"}
					// 		classNameChildrenWrapper="w-fit"
					// 	>
					// 		<InfoOutlined className="w-5 h-5" />
					// 	</Tooltip>
					// ) : statusKey in processingStatuses ? (
					// 	<Tooltip
					// 		label={processingStatuses[statusKey]}
					// 		classNameChildrenWrapper="w-fit"
					// 	>
					// 		<ProcessingStatusIcon status={statusKey} />
					// 	</Tooltip>
					// ) : (
					// 	'-'
					// )
				},
			},
			{
				accessorKey: 'category',
				enableResizing: true,
				enableSorting: true,
				minSize: 100,
				size: 100,
				sortingFn: 'alphanumeric',
				filterFn: objectIdFilter,
				header: ({ column }) => renderSortingHeader('Category', column),
				cell: (info) => (
					<Tooltip
						label={String(info.getValue() ?? '')}
						className={cn(isMobile && 'opacity-0')}
					>
						<p className="capitalize truncate">
							{String(info.getValue() ?? '')}
						</p>
					</Tooltip>
				),
			},
			{
				accessorKey: 'subcategory',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				sortingFn: 'alphanumeric',
				filterFn: objectIdFilter,
				header: ({ column }) => renderSortingHeader('Sub Category', column),
				cell: (info) => (
					<Tooltip
						label={String(info.getValue() ?? '')}
						className={cn(isMobile && 'opacity-0')}
					>
						<p className="capitalize truncate">
							{String(info.getValue() ?? '')}
						</p>
					</Tooltip>
				),
			},
			{
				accessorKey: 'type',
				enableResizing: true,
				enableSorting: true,
				minSize: 90,
				size: 90,
				sortingFn: 'alphanumeric',
				filterFn: typeFilter,
				header: ({ column }) => renderSortingHeader('Type', column),
				cell: (info) => String(info.getValue() ?? ''),
			},
			{
				accessorKey: 'size',
				enableResizing: true,
				enableSorting: true,
				minSize: 80,
				size: 80,
				sortingFn: 'alphanumeric',
				filterFn: sizeFilter,
				header: ({ column }) => renderSortingHeader('Size', column),
				cell: (info) =>
					String(formatDocumentSize(Number(info.getValue())) || '-'),
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				enableSorting: false,
				size: isMobile ? 50 : columnFilters?.length ? 50 : 30,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-end mr-2 gap-2 overflow-hidden min-h-full">
						{!isApp && (
							<IconButton
								variant="transparent"
								className="max-w-[20px] max-h-[20px] w-5 h-5 p-0 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<ResetColumnsSize className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
								}
								onClick={() => table.resetColumnSizing()}
								tooltip={'Reset columns sizes'}
							/>
						)}
						{!!columnFilters?.length && (
							<IconButton
								variant="transparent"
								className="max-w-[20px] max-h-[20px] w-5 h-5 p-0 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<FilterAltOff className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
								}
								onClick={() => onChangeFilters([])}
								tooltip={'Clean filters'}
							/>
						)}
					</div>
				),
				cell: ({ row }) => {
					const { id, isFavorite, type, name, isLocked, size, isSigned } = {
						id: row.original.id,
						isFavorite: row.original.options?.isFavorite,
						type: row?.original?.objectType,
						name: row?.original?.name,
						isLocked:
							row?.original?.options?.isLocked || lockedFiles[row.original.id],
						size: row?.original?.size,
						isSigned: row?.original?.options?.isSigned,
					}

					const isFileHasContent = Boolean(!type && size > 0)
					const isCommonItem = Boolean(type || isFileHasContent)
					const canSendToESign = isFileNameEsignSupported(name)

					return (
						<div className="bg-inherit md:absolute right-0 flex items-center justify-end gap-2 w-fit md:ml-auto pl-5 h-full group-hover:lg:left-custom-shadow">
							<div className="items-center justify-end hidden md:flex gap-2 opacity-0 lg:group-hover:opacity-100 transition-all duration-150 ease-in-out">
								{(isCommonItem
									? hasFeature(Features.Basic)
									: !type &&
										!size &&
										!isApp &&
										hasFeature(Features.Versions)) && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={
											isCommonItem || !hasFeature(Features.Versions) ? (
												<Visibility className="w-4 dark:fill-text-main-dark" />
											) : (
												<Edit className="w-4 dark:fill-text-main-dark" />
											)
										}
										onClick={() =>
											!type && !size && !isApp && hasFeature(Features.Versions)
												? handleEditDocument(row.original.id, row.original.name)
												: onPreview(id)
										}
										tooltip={
											type
												? 'Open folder'
												: size
													? 'Preview file'
													: hasFeature(Features.Versions)
														? 'Edit file'
														: 'Preview file'
										}
									/>
								)}

								{hasFeature(Features.Basic) && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant={isFavorite ? 'primary' : 'secondary'}
										icon={<Star className="w-4 dark:fill-text-main-dark" />}
										tooltip={
											isFavorite ? 'Remove from favorites' : 'Add to favorites'
										}
										onClick={() => {
											handleToggleFavorite({
												id,
												type,
												value: !isFavorite,
											})
										}}
									/>
								)}
								{hasFeature(Features.Basic) && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={<Delete className="w-4 dark:fill-text-main-dark" />}
										tooltip="Delete"
										disabled={isLocked}
										onClick={() =>
											onDeleteItem({
												id,
												name,
												type,
											})
										}
									/>
								)}
							</div>
							<OptionsMenuButton
								buttonClassName="ml-auto mr-10"
								parentScrollRef={containerRef}
								items={[
									{
										icon: <Visibility />,
										label: type ? 'Open folder' : 'Open in new tab',
										onClick: () => onPreview(id, null, true),
										visible:
											hasFeature(Features.Basic) && isCommonItem && !isApp,
									},
									{
										icon: <Visibility />,
										label: 'Open',
										onClick: () => onPreview(id, null),
										visible:
											hasFeature(Features.Basic) &&
											isFileHasContent &&
											isMobile,
									},
									{
										icon: <Edit />,
										label: 'Edit',
										onClick: () =>
											handleEditDocument(row.original.id, row.original.name),
										visible:
											!type &&
											hasFeature(Features.Versions) &&
											!isApp &&
											!isSigned,
										disabled: isLocked,
									},
									{
										icon: <LocalOffer />,
										label: 'Rename',
										onClick: () =>
											onEditItem({
												id,
												name: row?.original?.name,
												type: row?.original?.objectType,
											}),
										visible: true,
										disabled: isLocked,
									},
									{
										icon: <Delete />,
										label: 'Delete',
										onClick: () =>
											onDeleteItem({
												id,
												name: row?.original?.name,
												type: row?.original?.objectType,
											}),
										visible: true,
										disabled: isLocked,
										withDivider: true,
									},
									{
										icon: <InfoOutlined />,
										label: 'File info',
										onClick: () => onFileInfo(id),
										visible: !type,
									},
									{
										icon: <ManageHistory />,
										label: 'Versions',
										onClick: () => onConfigVersion(id),
										visible: hasFeature(Features.Versions) && isFileHasContent,
									},
									{
										icon: isFavorite ? <Star /> : <StarOutlineOutlined />,
										label: isFavorite ? 'Unstar' : 'Star',
										onClick: () =>
											handleToggleFavorite({
												id,
												type: row?.original?.objectType,
												value: !isFavorite,
											}),
										visible: true,
									},
									{
										icon: <Key />,
										label: 'Permissions',
										onClick: () => onConfigPermissions(id),
										visible: hasFeature(Features.ManageGroupsRolesPermissions),
										disabled: isLocked,
										withDivider: !type,
									},
									{
										icon: <Refresh />,
										label: 'Reclassify',
										onClick: () => onReclassify(id),
										visible:
											hasFeature(Features.Classification) && isCommonItem,
										disabled: isLocked,
									},
									{
										icon: <StickyNote2Sharp />,
										label: 'Notes',
										onClick: () => {
											onAddNote?.(id)
										},
										disabled: !onAddNote,
										visible: !type && hasFeature(Features.Collaboration),
									},
									{
										icon: <IosShareOutlined />,
										label: 'Share',
										onClick: () => onShareItem({ id, name, type }),
										visible: hasFeature(Features.Basic) && isFileHasContent,
										withDivider: !type && isSigned,
									},
									{
										icon: (
											<SigningSymbol className="w-5 h-5 text-text-main dark:text-text-main-dark" />
										),
										label: 'Send to eSign',
										onClick: () => onSendToESign?.(id),
										visible:
											hasFeature(Features.Basic) &&
											!type &&
											!isSigned &&
											canSendToESign &&
											!hideESignActions,
										withDivider: true,
									},
									{
										icon: <FileDownloadOutlined />,
										label: 'Download',
										onClick: () => onDownload(id),
										visible: hasFeature(Features.Basic) && isCommonItem,
									},
									{
										icon: <CopyAllOutlined />,
										label: 'Copy',
										onClick: () => onMove(id, 'copy'),
										visible:
											hasFeature(Features.Basic) && isCommonItem && !isSigned,
										disabled: isLocked,
									},
									{
										icon: <DriveFileMoveOutlined />,
										label: 'Move',
										onClick: () => onMove(id, 'move'),
										visible: hasFeature(Features.Basic) && isCommonItem,
										disabled: isLocked,
									},
									{
										icon: <Link />,
										label: 'Copy link',
										onClick: () => {
											onCopyLink?.(id, type)
										},
										disabled: !onCopyLink,
										visible: hasFeature(Features.Basic),
									},
								]}
							/>
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter(
				(columnDef): columnDef is AccessorKeyColumnDef<DataTableEntry, any> => {
					const available = ['id', 'name', 'options']
					selectedMobileColumn && available?.push(selectedMobileColumn)
					return (
						'accessorKey' in columnDef &&
						available.includes(columnDef.accessorKey)
					)
				},
			)
		}

		return base
	}, [
		data,
		checkedItems,
		lockedFiles,
		columnFilters,
		selectedMobileColumn,
		isMobile,
		onChangeFilters,
		hideESignActions,
		onSendToESign,
	])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		globalFilterFn: customGlobalFilter,
		manualSorting: false,
		enableSorting: true,
		enableSortingRemoval: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: (filters) => {
			const newFilters: CustomColumnFilter[] = (
				filters as ColumnFiltersState
			).map((filter) => ({
				id: filter.id,
				type: (filter.value as any)?.type ?? 0,
				value:
					(filter.value as any)?.type === 5
						? {
								startValue: (filter.value as any)?.value?.startValue ?? null,
								endValue: (filter.value as any)?.value?.endValue ?? null,
							}
						: ((filter.value as any)?.value ?? ''),
			}))

			onChangeFilters(newFilters)
		},
		state: {
			sorting,
			columnFilters: useMemo(
				() =>
					columnFilters.map((filter) => ({
						id: filter.id,
						value: {
							type: filter.type,
							value: filter.value,
							unit: filter?.unit,
						},
					})),
				[columnFilters],
			),
			globalFilter: searchQuery,
			pagination: {
				pageSize: limit,
				pageIndex: offset / limit,
			},
		},
	})

	useEffect(() => {
		if (isMobile) return
		table.getAllColumns().forEach((column) => {
			table
				.getColumn(column.id)
				?.toggleVisibility(visibleColumns.includes(column.id))
		})
	}, [visibleColumns, table])

	return { table }
}
